import type { TimelineElement } from '../types/timelineElement'
import type { ChecklistItem } from '../types/schema'
import type { DashboardRiskScore } from '../types/dashboardVisualItem'

const DEFAULT_RISK = 3
const MIN_RISK = 1
const MAX_RISK = 5

export const EMPTY_RISK_SCORE: DashboardRiskScore = {
  complexity: 0,
  urgency: 0,
  importance: 0,
  severity: 0,
  checklistCount: 0,
}

const normalizeRisk = (value: unknown) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_RISK
  return Math.max(MIN_RISK, Math.min(MAX_RISK, Math.round(value)))
}

export const getChecklistRiskScore = (item: ChecklistItem): DashboardRiskScore => {
  const complexity = normalizeRisk(item.complexityStars)
  const urgency = normalizeRisk(item.urgencyStars)
  const importance = normalizeRisk(item.importanceStars)
  return {
    complexity,
    urgency,
    importance,
    severity: Math.max(complexity, urgency, importance),
    checklistCount: 1,
  }
}

const maxRisk = (scores: DashboardRiskScore[]): DashboardRiskScore => {
  if (scores.length === 0) return EMPTY_RISK_SCORE
  return {
    complexity: Math.max(...scores.map((score) => score.complexity)),
    urgency: Math.max(...scores.map((score) => score.urgency)),
    importance: Math.max(...scores.map((score) => score.importance)),
    severity: Math.max(...scores.map((score) => score.severity)),
    checklistCount: scores.reduce((total, score) => total + score.checklistCount, 0),
  }
}

const buildDescendantElementIds = (
  elementId: string,
  childrenByParentId: ReadonlyMap<string | null, readonly TimelineElement[]>,
) => {
  const descendants = new Set<string>([elementId])
  const queue = [elementId]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    const children = childrenByParentId.get(current) ?? []
    for (const child of children) {
      if (descendants.has(child.id)) continue
      descendants.add(child.id)
      queue.push(child.id)
    }
  }
  return descendants
}

export const buildRiskByElementId = (
  elements: readonly TimelineElement[],
  checklistItems: readonly ChecklistItem[],
): Map<string, DashboardRiskScore> => {
  const childrenByParentId = new Map<string | null, TimelineElement[]>()
  for (const element of elements) {
    const siblings = childrenByParentId.get(element.parentId) ?? []
    siblings.push(element)
    childrenByParentId.set(element.parentId, siblings)
  }

  const checklistScoresByParentId = new Map<string, DashboardRiskScore[]>()
  for (const item of checklistItems) {
    if (item.nodeKind === 'GATE_AND' || item.nodeKind === 'GATE_OR') continue
    const scores = checklistScoresByParentId.get(item.parentContainerId) ?? []
    scores.push(getChecklistRiskScore(item))
    checklistScoresByParentId.set(item.parentContainerId, scores)
  }

  const result = new Map<string, DashboardRiskScore>()
  for (const element of elements) {
    const descendantIds = buildDescendantElementIds(element.id, childrenByParentId)
    const scores: DashboardRiskScore[] = []
    for (const descendantId of descendantIds) {
      scores.push(...(checklistScoresByParentId.get(descendantId) ?? []))
    }
    result.set(element.id, maxRisk(scores))
  }
  return result
}

